import { useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { useCompleteVulModalStore } from './CompleteVulModalStore';

import MLTextArea from 'components/Input/TextArea';
import { MLInput } from 'components/Input/Input';
import './CompleteVulModal.scss';
import { usePatchCompleteVul } from 'hooks/InteractiveMap/usePatchCompleteVul';
import { CompleteVulnerabilityFormValues, completeVulnerabilitySchema } from 'api/vulnerabilities/type';

interface CompleteEncModalProps {
  refetchVulnerabilities: () => void;
}

const getNowForDatetimeLocal = () => {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const CompleteVulModal: React.FC<CompleteEncModalProps> = ({ refetchVulnerabilities }) => {
  const { isOpen, onCompleteVulClose, vulId } = useCompleteVulModalStore();
  const { slug } = useParams<{ slug: string }>();
  const putCompleteEncounterMutation = usePatchCompleteVul();
  const { t } = useTranslation();

  const { handleSubmit, control, reset } = useForm<CompleteVulnerabilityFormValues>({
    resolver: zodResolver(completeVulnerabilitySchema),
    defaultValues: {
      fix_comment: '',
      fix_date: getNowForDatetimeLocal(),
      photos: [],
    },
  });

  const onReset = useCallback(() => {
    reset();
    onCompleteVulClose();
  }, [reset, onCompleteVulClose]);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} toggle={onReset} size="lg">
      <ModalHeader className="border-bottom py-3 " toggle={onReset}>
        <div className="text-purple">Resolve Vulnerability</div>
      </ModalHeader>
      <ModalBody>
        <div className="row mb-2 align-items-end">
          <div className="col-xl-12">
            <Controller
              control={control}
              name="fix_date"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  {...field}
                  type="datetime-local"
                  error={error?.message}
                  label="Resolve Date & Time"
                  required
                  value={field.value ? field.value.substring(0, 16) : ''}
                  onChange={e => field.onChange(e.target.value)}
                />
              )}
            />
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-xl-12">
            <Controller
              control={control}
              name="fix_comment"
              render={({ field, fieldState: { error } }) => (
                <MLTextArea
                  required
                  {...field}
                  className="form-control"
                  error={error?.message}
                  label="Comment"
                  placeholder="Comment sighting and give advices to client"
                  rows={3}
                />
              )}
            />
          </div>
        </div>
        <div className="row mb-3">
          <Controller
            name="photos"
            control={control}
            render={({ field: { onChange } }) => (
              <MLInput
                label={t('VulnerabilityModal.photo')}
                type="file"
                multiple
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const files = e.target.files ? Array.from(e.target.files) : [];
                  onChange(files);
                }}
              />
            )}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="danger" onClick={onReset} className="btn btn-ghost-danger">
          Close
        </Button>
        <Button
          color="success"
          onClick={handleSubmit(async formData => {
            putCompleteEncounterMutation.mutate(
              { vulnerabilityId: vulId, vul: formData, objectSlug: slug || '' },
              {
                onSuccess: () => {
                  refetchVulnerabilities();
                  onReset();
                },
              },
            );
          })}
          className="btn btn-ghost-success"
        >
          Resolve vulnerability
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CompleteVulModal;
